"use client";

import { useState, useEffect, useMemo } from "react";
import Image from "@/components/ui/OptimizedImage";
import Link from "next/link";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type HeroImage = {
  id: string;
  kind: string;
  url: string;
  label: string | null;
};

export interface HeroSectionProps {
  eventTitle: string;
  heroImages: HeroImage[];
  heroSlide: number;
  setHeroSlide: (slide: number | ((prev: number) => number)) => void;
  setGalleryIndex: (index: number) => void;
  setGalleryOpen: (open: boolean) => void;
  moodTag: string | null;
  dateLabel: string;
  timeLabel: string;
  isOnlineEvent: boolean;
  isEnded: boolean;
  isCancelled: boolean;
  venue: string;
  visibility?: "PUBLIC" | "PRIVATE";
  category?: string | null;
  accessControl?: "OPEN" | "INVITE_ONLY";
  eventStartDate?: string | null;
  eventStartTime?: string | null;
  headliner?: { name: string; imageUrl: string | null; role: string | null } | null;
}

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */

function CalendarIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M16 3v4M8 3v4M3 11h18" />
    </svg>
  );
}

function PinIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M12 21s7-6 7-11a7 7 0 10-14 0c0 5 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

function ChevronLeftIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function ChevronRightIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function HeroSection({
  eventTitle,
  heroImages,
  heroSlide,
  setHeroSlide,
  setGalleryIndex,
  setGalleryOpen,
  moodTag,
  dateLabel,
  timeLabel,
  isOnlineEvent,
  isEnded,
  isCancelled,
  venue,
  visibility,
  category,
  accessControl,
  eventStartDate,
  eventStartTime,
  headliner,
}: HeroSectionProps) {
  /* ---- Countdown timer ---- */
  const eventStartMs = useMemo(() => {
    const raw = eventStartTime ?? eventStartDate;
    if (!raw) return null;
    const ms = new Date(raw).getTime();
    return Number.isNaN(ms) ? null : ms;
  }, [eventStartDate, eventStartTime]);

  const [countdown, setCountdown] = useState<{ d: number; h: number; m: number; s: number } | null>(null);

  useEffect(() => {
    if (!eventStartMs) return;
    const tick = () => {
      const diff = eventStartMs - Date.now();
      if (diff <= 0) { setCountdown(null); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown({ d, h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [eventStartMs]);

  return (
    <>
      {/* HERO */}
      <section className="relative h-[50vh] min-h-[360px] lg:h-[72vh] lg:min-h-[500px] lg:max-h-[720px] w-full overflow-hidden">
        {/* Carousel slides */}
        {heroImages.length > 0 ? (
          <div className="absolute inset-0 flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${heroSlide * 100}%)` }}>
            {heroImages.map((img, idx) => (
              <div key={img.id} className="relative min-w-full h-full flex-shrink-0 cursor-pointer" onClick={() => { setGalleryIndex(idx); setGalleryOpen(true); }}>
                {/* Blurred background — low quality since it's behind a blur filter */}
                <Image src={img.url} alt={img.label || eventTitle} fill className="object-cover blur-2xl scale-110" sizes="(max-width: 768px) 50vw, 640px" aria-hidden="true" quality={10} placeholder="blur" blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiMwYjBjMGYiLz48L3N2Zz4=" />
                {/* Main hero image — only first slide gets priority */}
                <Image src={img.url} alt={img.label || eventTitle} fill className="object-contain" sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px" priority={idx === 0} fetchPriority={idx === 0 ? "high" : "low"} placeholder="blur" blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiMwYjBjMGYiLz48L3N2Zz4=" />
              </div>
            ))}
          </div>
        ) : (
          <div className="absolute inset-0 bg-[#0b0c0f]" />
        )}
        {/* Subtle bottom fade for clean transition */}
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#0b0c0f] to-transparent" />
        {/* Carousel nav arrows */}
        {heroImages.length > 1 ? (
          <>
            <button
              type="button"
              onClick={() => setHeroSlide((s: number) => Math.max(0, s - 1))}
              className={`absolute left-5 top-1/2 z-20 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full border border-white/[0.08] text-white/60 bg-[rgba(5,5,8,0.5)] backdrop-blur-sm hover:bg-[#0c0c18] hover:text-white transition ${heroSlide === 0 ? "opacity-30 pointer-events-none" : ""}`}
              aria-label="Previous slide"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => setHeroSlide((s: number) => Math.min(heroImages.length - 1, s + 1))}
              className={`absolute right-5 top-1/2 z-20 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full border border-white/[0.08] text-white/60 bg-[rgba(5,5,8,0.5)] backdrop-blur-sm hover:bg-[#0c0c18] hover:text-white transition ${heroSlide === heroImages.length - 1 ? "opacity-30 pointer-events-none" : ""}`}
              aria-label="Next slide"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
            {/* Thumbnail strip */}
            <div className="absolute bottom-28 right-7 z-20 hidden lg:flex items-center gap-1.5">
              {heroImages.slice(0, 5).map((img, idx) => (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => setHeroSlide(idx)}
                  className={`relative w-12 h-9 rounded-md overflow-hidden border-2 transition ${idx === heroSlide ? "border-white/60" : "border-transparent opacity-50 hover:opacity-80"}`}
                  aria-label={`Go to slide ${idx + 1}`}
                >
                  <Image src={img.url} alt="" fill className="object-cover" sizes="48px" />
                </button>
              ))}
            </div>
            {/* Counter */}
            <div className="absolute bottom-28 left-7 z-20 text-[13px] font-medium text-white/50 tabular-nums">
              {heroSlide + 1} / {heroImages.length}
            </div>
          </>
        ) : null}
        {/* Featured artist + View full image thumbnail */}
        <div className="absolute bottom-5 right-7 z-20 flex items-end gap-3">
          {headliner ? (
            <div className="flex items-center gap-2.5 rounded-full bg-[rgba(5,5,8,0.7)] backdrop-blur-md border border-white/[0.1] px-3 py-2">
              {headliner.imageUrl ? (
                <div className="relative w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border border-white/20">
                  <Image src={headliner.imageUrl} alt={headliner.name} fill className="object-cover" sizes="32px" />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center bg-white/10 text-[10px] font-bold text-white/50">
                  {headliner.name.slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.06em] text-white/40 leading-none">Featured</p>
                <p className="text-sm font-medium text-white leading-tight truncate max-w-[140px]">{headliner.name}</p>
              </div>
            </div>
          ) : null}
          {heroImages.length > 0 && heroImages[heroSlide] ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setGalleryIndex(heroSlide); setGalleryOpen(true); }}
              className="h-14 w-14 rounded-lg overflow-hidden border-2 border-white/20 shadow-lg hover:border-white/50 transition group flex-shrink-0"
              aria-label="View full image"
            >
              <Image src={heroImages[heroSlide].url} alt="" fill className="object-cover" sizes="56px" />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-white/80">
                  <path d="M13.28 7.78l3.22-3.22v2.69a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.69l-3.22 3.22a.75.75 0 001.06 1.06zM2 17.25v-4.5a.75.75 0 011.5 0v2.69l3.22-3.22a.75.75 0 011.06 1.06L4.56 16.5h2.69a.75.75 0 010 1.5h-4.5a.75.75 0 01-.75-.75z" />
                </svg>
              </div>
            </button>
          ) : null}
        </div>
      </section>

      {/* Hero content -- below image */}
      <div className="mx-auto max-w-[1200px] px-7 pt-6 pb-2">
        {/* Badges */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {moodTag ? (
            <span className="bg-[rgba(58,92,255,0.2)] text-[#4A8BFF] border border-[rgba(58,92,255,0.3)] px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em]">
              {moodTag}
            </span>
          ) : null}
          {visibility === "PUBLIC" ? (
            <span className="bg-[rgba(46,204,136,0.1)] text-emerald-400 border border-[rgba(46,204,136,0.18)] px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em]">
              Public
            </span>
          ) : null}
          {visibility === "PRIVATE" ? (
            <span className="bg-white/[0.04] text-white/60 border border-white/[0.08] px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em]" role="status" aria-label="Private event">
              Private
            </span>
          ) : null}
          {category ? (
            <Link
              href={`/?category=${encodeURIComponent(category)}`}
              className="bg-white/[0.04] text-white/60 border border-white/[0.08] px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] transition hover:text-white hover:border-white/[0.12]"
            >
              {category}
            </Link>
          ) : null}
          {isOnlineEvent ? (
            <span className="bg-[rgba(58,92,255,0.2)] text-[#4A8BFF] border border-[rgba(58,92,255,0.3)] px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em]">
              Online
            </span>
          ) : null}
          {isEnded ? (
            <span className="bg-white/[0.04] text-white/60 border border-white/[0.08] px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em]" role="status" aria-label="Event has ended">
              Ended
            </span>
          ) : isCancelled ? (
            <span className="bg-white/[0.04] text-white/60 border border-white/[0.08] px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em]" role="status" aria-label="Event cancelled">
              Cancelled
            </span>
          ) : null}
          {accessControl === "INVITE_ONLY" ? (
            <span className="bg-white/[0.04] text-white/60 border border-white/[0.08] px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em]" role="status" aria-label="Invite-only event">
              Invite Only
            </span>
          ) : null}
        </div>
        {/* Title + Countdown */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 lg:gap-4">
          <h1 className="font-light text-[clamp(2rem,4.8vw,3.5rem)] leading-[1.1] tracking-[-0.02em] text-white max-w-[900px]">
            {eventTitle}
          </h1>
          {countdown && !isEnded && !isCancelled ? (
            <div className="flex-shrink-0 flex items-end gap-1.5 tabular-nums mt-1 lg:mt-0 lg:pt-2">
              <span className="text-xs uppercase tracking-[0.08em] text-white/40 mr-1 pb-0.5">Starts in</span>
              {countdown.d > 0 ? <><span className="text-2xl text-white font-semibold">{countdown.d}</span><span className="text-sm text-white/40 pb-0.5">d</span></> : null}
              <span className="text-2xl text-white font-semibold">{String(countdown.h).padStart(2, "0")}</span><span className="text-sm text-white/40 pb-0.5">h</span>
              <span className="text-2xl text-white font-semibold">{String(countdown.m).padStart(2, "0")}</span><span className="text-sm text-white/40 pb-0.5">m</span>
              <span className="text-2xl text-white font-semibold">{String(countdown.s).padStart(2, "0")}</span><span className="text-sm text-white/40 pb-0.5">s</span>
            </div>
          ) : null}
        </div>
        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 mt-4 text-white/60 text-sm">
          <span className="flex items-center gap-1.5">
            <CalendarIcon className="h-4 w-4" />
            {dateLabel}
          </span>
          <span className="w-[3px] h-[3px] rounded-full bg-white/30" />
          <span className="flex items-center gap-1.5">
            {timeLabel}
          </span>
          <span className="w-[3px] h-[3px] rounded-full bg-white/30" />
          <span className="flex items-center gap-1.5">
            <PinIcon className="h-4 w-4" />
            {isOnlineEvent ? "Online" : venue}
          </span>
        </div>
      </div>
    </>
  );
}
